import { InteractionType, DEBOUNCED_TYPES } from './trackInteractionTypes';
import { buildInteractionEvent, pushToGA4, __setGA4ConfigForTesting__, getScrollPercent } from './trackInteractionCore';
import { markMeaningfulInteraction } from './trackQualitySession';

const DEBOUNCE_MS = 500;
const debounceTimers: Map<string, ReturnType<typeof setTimeout>> = new Map();

// Interaction types that signal genuine user engagement (not passive clicks).
// Used by quality_session tracking to detect engaged users for ad optimization.
const MEANINGFUL_TYPES: ReadonlySet<InteractionType> = new Set([
  'calculator_interact', 'form_submit', 'form_focus', 'video_play',
]);

/**
 * Track a user interaction and send it to GA4 via the Measurement Protocol.
 *
 * For click events, prefer data attributes (`data-track-id`, `data-track-section`, `data-track-type`)
 * which are detected automatically by the global listener. Use this function directly for non-click
 * events like video play, calculator input changes, form focus, and carousel slides.
 */
export const trackInteraction = (
  id: string,
  section: string,
  type: InteractionType,
  element?: HTMLElement | null
): void => {
  if (typeof window === 'undefined') return;

  if (MEANINGFUL_TYPES.has(type)) {
    markMeaningfulInteraction();
  }

  const eventData = buildInteractionEvent(id, section, type, element);

  if (DEBOUNCED_TYPES.has(type)) {
    const key = `${id}:${type}`;
    const existing = debounceTimers.get(key);
    if (existing) clearTimeout(existing);
    debounceTimers.set(
      key,
      setTimeout(() => {
        pushToGA4(eventData);
        debounceTimers.delete(key);
      }, DEBOUNCE_MS)
    );
  } else {
    pushToGA4(eventData);
  }
};

// Re-export for use by the global click listener and other modules
export { buildInteractionEvent, pushToGA4, __setGA4ConfigForTesting__, getScrollPercent };
